import { Picture } from '@app/components/Picture';
import { LinkCopiedToast } from '@app/components/Toasts/LinkCopiedToast';
import Avatar from '@redbubble/design-system/src/Avatar';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import ShareIcon from '@redbubble/design-system/src/Icons/Share';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import logger from '@redbubble/next/logger';
import React from 'react';
import styles from './ArtistHeader.module.scss';
import { ArtistHeaderProps, BannerUrls } from './types';

const loadToast = () => import('react-toastify').then((module) => module.toast);

const messages = defineMessages({
  bannerAltText: {
    defaultMessage: 'Artist shop banner image',
  },
  totalWorks: {
    defaultMessage: `{worksCount, plural,
       =0 {0 designs}
       one {1 design}
       other {{worksCount} designs}}`,
  },
  viewProfile: {
    defaultMessage: 'View artist profile',
  },
  share: {
    defaultMessage: 'Share',
  },
});

const getBannerSrcSet = (bannerUrls: BannerUrls) =>
  bannerUrls
    .map((banner) => {
      const width = banner.size?.split('x')[0] ?? '2400';
      return `${banner.url} ${width}w`;
    })
    .join(', ');

const getBannerFallbackUrl = (bannerUrls: BannerUrls) =>
  bannerUrls.find((banner) => banner.size === '2400x600')?.url ??
  bannerUrls[0]?.url;

const handleShare = async () => {
  const shareUrl = window.location.href;

  await navigator.clipboard
    .writeText(shareUrl)
    .then(async () => {
      const toast = await loadToast();
      toast(<LinkCopiedToast />);
    })
    .catch((err) => {
      const message = 'Failed to copy share link to clipboard';
      logger.error({ err, message, context: 'ArtistHeader' }, message);
    });
};

export const ArtistHeader: React.FC<ArtistHeaderProps> = ({ artistInfo }) => {
  const intl = useIntl();
  const {
    avatar,
    bannerUrls,
    shopDisplayName,
    additionalInfo,
    worksCount,
    username,
  } = artistInfo;

  const displayName = shopDisplayName ?? username;

  return (
    <Box element="section">
      <Box className={styles.artistBanner}>
        {bannerUrls.length > 0 && (
          <Picture fillContainer>
            <Picture.Source
              srcSet={getBannerSrcSet(bannerUrls)}
              type="image/jpeg"
              sizes="(max-width: 480px) 600px, (max-width: 1024px) 1200px, 2400px"
            />
            <Picture.Img
              src={getBannerFallbackUrl(bannerUrls)}
              alt={intl.formatMessage(messages.bannerAltText)}
              loading="eager"
            />
          </Picture>
        )}
      </Box>
      <Box
        className={styles.artistInfoContainer}
        display="flex"
        flexDirection="row"
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          marginLeft="m"
          marginRight="m"
          className={styles.artistInfoContent}
        >
          <Box className={styles.avatarWrapper}>
            {avatar && (
              <Box className={styles.avatar}>
                <Avatar
                  src={avatar}
                  size={constants.LARGE}
                  bordered={{ color: 'var(--ds-color-background-ui)' }}
                />
              </Box>
            )}
          </Box>
          <Box className={styles.artistName}>
            <Text type="display2" element="h1">
              {displayName}
            </Text>
          </Box>
          <Box className={styles.artistInfoList} element="ul">
            <Box className={styles.artistInfoItem}>
              <Text muted type="body2" element="li">
                {additionalInfo}
              </Text>
            </Box>
            {worksCount !== undefined && worksCount > 0 && (
              <Box className={styles.artistInfoItem}>
                <Text muted type="body2" element="li">
                  {intl.formatMessage(messages.totalWorks, { worksCount })}
                </Text>
              </Box>
            )}
            <Box className={styles.artistProfileItem}>
              <Text type="body2" element="li">
                <a className={styles.artistProfileLink} href="#profile">
                  {intl.formatMessage(messages.viewProfile)}
                </a>
              </Text>
            </Box>
          </Box>
        </Box>
        <Box className={styles.shareButton}>
          <Button
            onClick={handleShare}
            size={constants.SMALL}
            strong
            iconBefore={<ShareIcon size={constants.MEDIUM} />}
          >
            {intl.formatMessage(messages.share)}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
